import { RefObject, useMemo, useRef, useState, useCallback } from "react";

import {
    Button,
    Column,
    Icon,
    IconButton,
    LabelWrapper,
    OutlinedCard,
    Row,
    Text,
    ToggleButtons,
    useAppPalette,
    useCopyToClipboard
} from "@bridgesplit/ui";
import { colorToAlpha, Result } from "@bridgesplit/utils";
import { BS_SHORT_URL_INVITE } from "app/constants";
import { useAlert } from "@bridgesplit/react";

import SocialShareCard from "./SocialShareCard";
import { exportSvgtoPng, getOverlaysWithPalette } from "./util";
import { CARD_DIMS, DEFAULT_OVERLAYS, OVERLAYS, SOCIAL_SHARE_PALETTES, DEFAULT_TWITTER_TEXT } from "./constants";
import { OverlaySelectorProps, SocialShareDialogProps, Overlay, SocialShareStat } from "./types";

export default function SocialShareContent(props: SocialShareDialogProps & { cardSize: "sm" | "md" | "lg" }) {
    const { refCode, position, stats, cardSize } = props;
    const { width, height } = CARD_DIMS[cardSize];

    const cardRef: RefObject<HTMLDivElement> = useRef(null);

    const [headlineIdx, setHeadlineIdx] = useState(0);
    const candidates = useMemo(() => stats.filter((s) => s.headline === true), [stats]);
    const headline = candidates[headlineIdx] ?? candidates[0] ?? stats[0];

    const variant = headline.isNegative ? "negative" : "positive";
    const palette = SOCIAL_SHARE_PALETTES[variant];
    const overlays = useMemo(() => getOverlaysWithPalette({ overlays: OVERLAYS, palette: palette.overlay }), [palette]);
    const [overlay, setOverlay] = useState<Overlay>(DEFAULT_OVERLAYS[variant]);

    return (
        <Row
            spacing={2}
            height="100%"
            sx={{
                gap: 2,
                flexDirection: cardSize !== "lg" ? "column-reverse" : "row",
                alignItems: cardSize !== "lg" ? "stretch" : "top"
            }}
        >
            <Column spacing={3}>
                <HeadlineSelect value={headlineIdx} onChange={setHeadlineIdx} options={candidates} />
                <OverlaySelector
                    buttonSize={cardSize !== "lg" ? 56 : 48}
                    overlays={overlays}
                    selected={overlay}
                    onSelect={setOverlay}
                />

                {refCode && <ReferralCode code={refCode} />}
                <Cta cardRef={cardRef} postText={DEFAULT_TWITTER_TEXT} />
            </Column>
            <Column spacing={2}>
                <OutlinedCard padding={0} sx={{ overflow: "hidden", minWidth: "max-content", maxWidth: "max-content" }}>
                    <SocialShareCard
                        headline={headline.value}
                        palette={palette}
                        overlay={overlays.find((o) => o.id === overlay)?.svg}
                        position={position}
                        stats={stats}
                        width={width}
                        height={height}
                        ref={cardRef}
                    />
                </OutlinedCard>
            </Column>
        </Row>
    );
}

function HeadlineSelect({
    value,
    onChange,
    options
}: {
    value: number;
    onChange: (i: number) => void;
    options: SocialShareStat[];
}) {
    return (
        <LabelWrapper label="Headline">
            <ToggleButtons
                value={value}
                setValue={onChange}
                options={options.map((s, i) => ({ value: i, label: s.label }))}
            />
        </LabelWrapper>
    );
}

function Cta({
    cardRef,
    postText = DEFAULT_TWITTER_TEXT
}: {
    cardRef: RefObject<HTMLElement>;
    postText: string | undefined;
}) {
    const { handleShare, handleDownload, handleCopy } = useImageHandlers(cardRef, postText);
    return (
        <Column spacing={1}>
            <Button
                fullWidth
                sx={{ minWidth: "max-content", px: 2 }}
                variant="default"
                onClick={handleShare}
                startIcon={<Icon type="share" />}
            >
                Share post
            </Button>
            <Row spacing={1}>
                <Button
                    fullWidth
                    sx={{ minWidth: "max-content", px: 2 }}
                    variant="contained"
                    onClick={handleDownload}
                    startIcon={<Icon type="download" />}
                >
                    Download image
                </Button>
                <Button
                    fullWidth
                    sx={{ minWidth: "max-content", px: 2 }}
                    variant="contained"
                    onClick={handleCopy}
                    startIcon={<Icon type="copy" />}
                >
                    Copy image
                </Button>
            </Row>
        </Column>
    );
}

function ReferralCode({ code }: { code: string }) {
    const copyToClipboard = useCopyToClipboard();

    const handleCopy = async () => {
        if (!code) return;
        await copyToClipboard(`https://${BS_SHORT_URL_INVITE}/${code}`, {
            successMessage: `${code} copied to clipboard`
        });
    };

    return (
        <LabelWrapper label="Referral link">
            <OutlinedCard sx={{ py: 1 }} backgroundColor="transparent">
                <Row spaceBetween>
                    <Text color="disabled">{`${BS_SHORT_URL_INVITE}/${code}`}</Text>
                    <IconButton onClick={handleCopy} border={false} icon="copy" textVariant="body2" />
                </Row>
            </OutlinedCard>
        </LabelWrapper>
    );
}

function OverlaySelector({ overlays, selected, onSelect, buttonSize = 48 }: OverlaySelectorProps) {
    const { primary, divider } = useAppPalette();
    const sxBtn = (sel: boolean) => ({
        p: 0,
        width: buttonSize,
        height: buttonSize,
        minWidth: buttonSize,
        maxWidth: buttonSize,
        minHeight: buttonSize,
        maxHeight: buttonSize,
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${sel ? colorToAlpha(primary, 0.4) : divider}`,
        display: "flex",
        backgroundColor: sel ? divider : "transparent",
        alignItems: "center",
        justifyContent: "center"
    });
    return (
        <LabelWrapper label="Choose overlay">
            <Row spacing={1} sx={{ mt: 0.25 }} wrap>
                {overlays.map(({ id, svg }) => (
                    <Button key={id} onClick={() => onSelect(id)} sx={sxBtn(selected === id)} aria-label={id}>
                        <svg
                            width={buttonSize}
                            height={buttonSize}
                            viewBox="0 0 500 500"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            {svg}
                        </svg>
                    </Button>
                ))}
            </Row>
        </LabelWrapper>
    );
}

/**
 * useImageHandlers
 * Returns handlers for downloading, copying, and sharing an image.
 * @param cardRef - ref to the SVG/element to export
 * @param postText - text to use for sharing
 */
function useImageHandlers(cardRef: RefObject<HTMLElement>, postText?: string) {
    const { alert } = useAlert();

    const handleDownload = useCallback(async () => {
        if (!cardRef.current) {
            alert("Failed to download image: no card reference found", "error");
            return Result.err("No card ref");
        }
        const dataUrl = await exportSvgtoPng(cardRef);
        if (!dataUrl) {
            alert("Failed to download image", "error");
            return Result.err("No data URL");
        }
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = "portfolio-share.png";
        a.click();
        return Result.ok();
    }, [cardRef, alert]);

    const handleCopy = useCallback(async () => {
        try {
            if (!cardRef.current) {
                alert("Failed to copy image: no card reference found", "error");
                return Result.err("No card ref");
            }
            const dataUrl = await exportSvgtoPng(cardRef);
            if (!dataUrl) {
                alert("Failed to copy image", "error");
                return Result.err("No data URL");
            }

            const res = await fetch(dataUrl);
            const blob = await res.blob();

            // Write to clipboard as PNG image
            await navigator.clipboard.write([
                new ClipboardItem({
                    "image/png": blob
                })
            ]);

            alert("Image copied to clipboard", "success");
            return Result.ok();
        } catch (error) {
            alert("Failed to copy image to clipboard", "error");
            return Result.err("Failed to copy image to clipboard");
        }
    }, [cardRef, alert]);

    const handleShare = useCallback(() => {
        const urlText = encodeURIComponent(postText ?? DEFAULT_TWITTER_TEXT);
        window.open(`https://x.com/intent/post?text=${urlText}`, "_blank");
    }, [postText]);

    return {
        handleDownload,
        handleCopy,
        handleShare
    };
}
